import { ActionIcon, Badge, Group, Text, Tooltip } from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useLayoutEffect, useRef, useState } from "react";

export const ADMIN_BADGE_COLORS = {
    role: "green",
    datasource: "violet",
    tag: "gray",
} as const;

function BadgeWithTooltip({
    label,
    description,
    color,
}: {
    label: string;
    description?: string;
    color: string;
}) {
    const badge = (
        <Badge size="sm" variant="light" color={color} radius="sm" style={{ flexShrink: 0 }}>
            {label}
        </Badge>
    );

    if (!description) {
        return badge;
    }

    return (
        <Tooltip label={description} withArrow multiline maw={300}>
            {badge}
        </Tooltip>
    );
}

function BadgeList({
    items,
    color,
    descriptions,
}: {
    items: string[];
    color: string;
    descriptions?: Record<string, string>;
}) {
    return items.map((item) => (
        <BadgeWithTooltip
            key={item}
            label={item}
            description={descriptions?.[item]}
            color={color}
        />
    ));
}

// Badges rendered on a single line with an expand toggle that appears only when
// the badges overflow the available width. Keeps table rows the same height.
function ExpandableBadges({
    items,
    color,
    descriptions,
}: {
    items: string[];
    color: string;
    descriptions?: Record<string, string>;
}) {
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const checkOverflow = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
        checkOverflow();

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Group gap={4} wrap="nowrap" align="flex-start" style={{ width: "100%" }}>
            <Group
                ref={containerRef}
                gap={4}
                wrap={expanded ? "wrap" : "nowrap"}
                style={{ overflow: "hidden", flex: 1, minWidth: 0 }}
            >
                <BadgeList items={items} color={color} descriptions={descriptions} />
            </Group>
            {(overflowing || expanded) && (
                <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((v) => !v);
                    }}
                    aria-label={expanded ? "Collapse" : "Expand"}
                    style={{ flexShrink: 0 }}
                >
                    {expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </ActionIcon>
            )}
        </Group>
    );
}

export function StringBadges({
    items,
    color,
    descriptions,
    singleLine = false,
}: {
    items: string[];
    color: string;
    descriptions?: Record<string, string>;
    singleLine?: boolean;
}) {
    if (!items?.length) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        );
    }

    if (singleLine) {
        return <ExpandableBadges items={items} color={color} descriptions={descriptions} />;
    }

    return (
        <Group gap={4}>
            <BadgeList items={items} color={color} descriptions={descriptions} />
        </Group>
    );
}

export function RoleIdBadge({ id, description }: { id: string; description?: string }) {
    return (
        <BadgeWithTooltip label={id} description={description} color={ADMIN_BADGE_COLORS.role} />
    );
}

export function DatasourceBadges({
    items,
    descriptions,
}: {
    items: string[];
    descriptions?: Record<string, string>;
}) {
    if (!items?.length) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        );
    }

    return (
        <Group gap={4}>
            {items.map((item) => (
                <BadgeWithTooltip
                    key={item}
                    label={item}
                    description={descriptions?.[item]}
                    color={ADMIN_BADGE_COLORS.datasource}
                />
            ))}
        </Group>
    );
}
